import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { Providers } from './providers/provider';
import { AppLayout } from './components/layouts/app-layout';
import { AuthGuard } from './components/auth/AuthGuard';
import { Spinner } from './components/ui/spinner';

// Lazy load pages for better performance
const DashboardContent = lazy(() => import('./components/dashboard/dashboard/dashboard-content').then(mod => ({ default: mod.DashboardContent })));
const AccountContent = lazy(() => import('./components/dashboard/account-content').then(mod => ({ default: mod.AccountContent })));
const AgentsContent = lazy(() => import('./components/dashboard/agents/agents-content').then(mod => ({ default: mod.AgentsContent })));
const AgentDetailPage = lazy(() => import('./pages/agents/AgentDetailPage'));
const UsageContext = lazy(() => import('./components/dashboard/usage/usage-context'));
const MessagingContent = lazy(() => import('./components/dashboard/messaging/messaging-context').then(mod => ({ default: mod.MessagingContent })));
const DocumentationContent = lazy(() => import('./pages/documentation/documentation-content'));

// Calendar pages
const CalendarRedirect = lazy(() => import('./components/dashboard/calendar/CalendarRedirect'));
const CalendarViewByIdClient = lazy(() => import('./components/dashboard/calendar/routes/CalendarViewByIdClient'));
const CalendarCreateClient = lazy(() => import('./components/dashboard/calendar/routes/CalendarCreateClient'));
const CalendarEditClient = lazy(() => import('./components/dashboard/calendar/routes/CalendarEditClient'));
const CalendarAppLayoutClient = lazy(() => import('./components/dashboard/calendar/routes/CalendarAppLayoutClient'));

// Loading component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <Spinner className="h-8 w-8" />
  </div>
);

// Wrapper components to pass params as props
function CalendarViewWrapper() {
  const { id } = useParams();
  return <CalendarViewByIdClient id={id} />;
}

function CalendarEditWrapper() {
  const { id } = useParams();
  return <CalendarEditClient id={id} />;
}

function App() {
  return (
    <Providers>
      <AuthGuard>
        <AppLayout>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Dashboard */}
              <Route path="/" element={<DashboardContent />} />
              
              {/* Account */}
              <Route path="/account" element={<AccountContent />} />
              
              {/* Agents */}
              <Route path="/agents" element={<AgentsContent pathname="/agents" />} />
              <Route path="/agents/:id" element={<AgentDetailPage />} />
              
              {/* Usage */}
              <Route path="/usage" element={<UsageContext />} />
              
              {/* Messaging - redirect base to instagram */}
              <Route path="/messaging" element={<Navigate to="/messaging/instagram" replace />} />
              <Route path="/messaging/instagram" element={<MessagingContent pathname="/messaging/instagram" />} />
              <Route path="/messaging/whatsapp" element={<MessagingContent pathname="/messaging/whatsapp" />} />
              <Route path="/messaging/messenger" element={<MessagingContent pathname="/messaging/messenger" />} />
              <Route path="/messaging/viber" element={<MessagingContent pathname="/messaging/viber" />} />
              
              {/* Calendar - wrapped in layout */}
              <Route path="/calendar" element={<CalendarAppLayoutClient><CalendarRedirect /></CalendarAppLayoutClient>} />
              <Route path="/calendar/create" element={<CalendarAppLayoutClient><CalendarCreateClient /></CalendarAppLayoutClient>} />
              <Route path="/calendar/:id" element={<CalendarAppLayoutClient><CalendarViewWrapper /></CalendarAppLayoutClient>} />
              <Route path="/calendar/:id/edit" element={<CalendarAppLayoutClient><CalendarEditWrapper /></CalendarAppLayoutClient>} />
              
              {/* Documentation */}
              <Route path="/documentation" element={<DocumentationContent />} />
              
              {/* Catch all - redirect to home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </AppLayout>
      </AuthGuard>
    </Providers>
  );
}

export default App;
